import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronDown, LayoutGrid, Plus, Search } from "lucide-react";
import { loadHierarchy, type HierarchyCatalog } from "./api";
import { hierarchyDestination, hierarchyUrl } from "./urls";

/** Workspace names come only from a fresh, account-authorized catalog. */
export function WorkspaceDropdown({ accountId, align = "left" }: {
  accountId?: string;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ accountId?: string; catalog?: HierarchyCatalog; failed?: boolean }>();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const panelId = useId();

  useEffect(() => {
    const controller = new AbortController();
    setState(undefined);
    void loadHierarchy({ accountId }, controller.signal).then(
      catalog => { if (!controller.signal.aborted) setState({ accountId, catalog }); },
      () => { if (!controller.signal.aborted) setState({ accountId, failed: true }); },
    );
    const refresh = () => { controller.abort(); setState(undefined); setRevision(value => value + 1); };
    window.addEventListener("focus", refresh);
    return () => { controller.abort(); window.removeEventListener("focus", refresh); };
  }, [accountId, revision]);

  useEffect(() => {
    if (!open) return;
    search.current?.focus();
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  const currentState = state?.accountId === accountId ? state : undefined;
  const catalog = currentState?.catalog;
  const path = hierarchyDestination(window.location.pathname)?.path;
  const current = catalog?.workspaces.find(workspace => {
    const url = hierarchyUrl(workspace.accountId, workspace.id);
    return path === url || path?.startsWith(`${url}/`);
  });
  const filtered = catalog?.workspaces.filter(workspace => workspace.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())) ?? [];
  const canCreate = catalog?.scope.role === "owner" || catalog?.scope.role === "manager";

  function close() { setOpen(false); trigger.current?.focus(); }
  function keyboard(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault(); event.stopPropagation(); close();
    }
    if (!open || !["ArrowDown", "ArrowUp"].includes(event.key)) return;
    const items = Array.from(root.current?.querySelectorAll<HTMLElement>("[data-workspace-option]") ?? []);
    if (!items.length) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = event.key === "ArrowDown" ? (index + 1) % items.length : (index <= 0 ? items.length - 1 : index - 1);
    items[next]?.focus();
  }

  return <div ref={root} className="workspace-dropdown" onKeyDown={keyboard} onBlur={event => {
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
  }}>
    <button ref={trigger} type="button" className="workspace-trigger" aria-label={`Switch workspace${current ? `, current workspace ${current.name}` : ""}`}
      aria-expanded={open} aria-haspopup="dialog" aria-controls={panelId}
      onClick={() => {
        if (!open) { setQuery(""); setState(undefined); setRevision(value => value + 1); }
        setOpen(value => !value);
      }}>
      <span className="workspace-avatar" aria-hidden="true">{current ? current.name.trim().charAt(0).toUpperCase() : <LayoutGrid size={16} />}</span>
      <span className="min-w-0 flex-1 truncate">{current?.name ?? "Choose workspace"}</span>
      <ChevronDown size={15} className={open ? "rotate-180" : ""} aria-hidden="true" />
    </button>
    {open ? <div id={panelId} role="dialog" aria-label="Choose workspace" className={`workspace-popover workspace-popover-${align}`}>
      <div className="px-4 pb-3 pt-4">
        <p className="text-sm font-semibold">Your workspaces</p>
        <p className="mt-1 text-xs text-[#68717c]">A place for your projects and conversations.</p>
      </div>
      <div className="relative mx-2 mb-2">
        <Search size={15} className="pointer-events-none absolute left-3 top-3 text-[#68717c]" aria-hidden="true" />
        <input ref={search} aria-label="Search workspaces" type="search" placeholder="Find a workspace…" value={query}
          onChange={event => setQuery(event.target.value)} className="workspace-search" />
      </div>
      <div className="workspace-options">
        {!currentState ? <p role="status" className="px-3 py-6 text-center text-sm text-[#68717c]">Loading workspaces…</p> : currentState.failed ? <div className="px-3 py-4 text-sm">
          <p role="alert">Workspaces couldn't be loaded.</p>
          <button type="button" className="mt-2 font-medium underline" data-workspace-option onClick={() => setRevision(value => value + 1)}>Try again</button>
        </div> : filtered.length ? filtered.map(workspace => <a key={workspace.id} data-workspace-option className="workspace-option"
          href={hierarchyUrl(workspace.accountId, workspace.id)} aria-current={current?.id === workspace.id ? "page" : undefined}
          onClick={event => { if (path === hierarchyUrl(workspace.accountId, workspace.id)) { event.preventDefault(); close(); } }}>
          <span className="workspace-avatar" aria-hidden="true">{workspace.name.trim().charAt(0).toUpperCase() || "W"}</span>
          <span className="min-w-0 flex-1 truncate">{workspace.name}</span>
          {current?.id === workspace.id ? <Check size={16} className="text-[#405de6]" aria-hidden="true" /> : null}
        </a>) : <p className="px-3 py-6 text-center text-sm text-[#68717c]">{query.trim() ? "No matching workspaces" : "No workspaces yet"}</p>}
      </div>
      <div className="border-t border-[#e8eaee] p-2">
        <a href={catalog ? hierarchyUrl(catalog.scope.accountId) : "/spaces"} className="workspace-option" data-workspace-option><LayoutGrid size={16} aria-hidden="true" />All workspaces</a>
        {canCreate ? <a href={`${hierarchyUrl(catalog!.scope.accountId)}#create-workspace`} className="workspace-option" data-workspace-option><Plus size={17} aria-hidden="true" />New workspace</a> : null}
      </div>
    </div> : null}
  </div>;
}
